import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { db, auth } from '../../../firebase';
import { CreditCard, Info } from 'lucide-react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
// import { useLocation } from 'react-router-dom';
// import { getFunctions, httpsCallable } from 'firebase/functions'; // Added for calling backend cloud functions
import '../membership_payment_page/membership_payment_page.css';

const PaymentPage = () => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null); 
  const [isProcessing, setIsProcessing] = useState(false);
  
  // 🟢 NEW SELECTIVE PAYMENT MODES MATRIX
  // Options: 'registration' | 'renewal' | 'tunggakan' | 'other'
  const [paymentType, setPaymentType] = useState('registration');
  const [years, setYears] = useState(1);
  
  // State elements for the custom 'other' payment framework option
  const [customPurpose, setCustomPurpose] = useState('');
  const [customAmount, setCustomAmount] = useState('');

  // =========================================================================
  // HOOK 1: Persistent Auth & Profile Fetcher
  // =========================================================================
  // useEffect(() => {
  //   const unsubscribe = onAuthStateChanged(auth, async (user) => {
  //     if (user) {
  //       try {
  //         const userRef = doc(db, "registrations", user.uid);
  //         const userSnap = await getDoc(userRef);

  //         if (userSnap.exists()) {
  //           const data = userSnap.data();
  //           setUserData(data);
            
  //           // 🟢 AUTO-ROUTING LOCK: Enforce Payment Category selection bounds
  //           if (data.status === 'verified' && data.parmNo) {
  //             setPaymentType('renewal'); // Default choice unlocked for active members
  //           } else {
  //             setPaymentType('registration'); // Hard lock for newcomers
  //           }
  //         }
  //       } catch (error) {
  //         console.error("Error fetching user data:", error);
  //       } finally {
  //         setLoading(false);
  //       }
  //     } else {
  //       setLoading(false);
  //     }
  //   });

  //   return () => unsubscribe();
  // }, []);

  useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        const userRef = doc(db, "registrations", user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData(data);
          
          // Check if they belong to a locked non-expiring tier
          const typeStr = data.memberType || '';
          const isTierLocked = typeStr.includes('Lifetime Members') || typeStr.includes('Ahli Seumur Hidup') || typeStr.includes('Corporate Members') || typeStr.includes('Ahli Korporat');

          // 🟢 AUTO-ROUTING LOCK
          if (data.status === 'verified' && data.parmNo) {
            // If they are Lifetime/Corporate, default them to 'other'. Otherwise, default to 'renewal'.
            setPaymentType(isTierLocked ? 'other' : 'renewal'); 
          } else {
            setPaymentType('registration'); 
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  });

  return () => unsubscribe();
}, []);

  // Check verification bounds explicitly
  const isVerifiedMember = useMemo(() => {
    return !!(userData?.status === 'verified' && userData?.parmNo);
  }, [userData]);

  // =========================================================================
  // MEMOIZED PRICE ENGINE CALCULATORS
  // =========================================================================
  const fees = useMemo(() => {
    const isStudent = userData?.memberType === 'Student Members' || userData?.memberType === 'Student' || userData?.memberType === 'Ahli Pelajar' || userData?.memberType === 'Pelajar';
    return {
      registration: isStudent ? 10 : 50,
      yearly: {
        'Professional Members': 100, 'Ordinary Members': 100, 'Corporate Members': 500,
        'Veteran Members': 25, 'Lifetime Members': 1000, 'Student Members': 25,
        'Professional': 100, 'Ordinary': 100, 'Corporate': 500, 'Veteran': 25,
        'Lifetime': 1000, 'Student': 25,
        'Ahli Profesional': 100, 'Ahli Biasa': 100, 'Ahli Korporat': 500,
        'Ahli Veteran': 25, 'Ahli Seumur Hidup': 1000, 'Ahli Pelajar': 25,
        'Profesional': 100, 'Biasa': 100, 'Korporat': 500,
        'Seumur Hidup': 1000, 'Pelajar': 25,
      }
    };
  }, [userData]);

  const isLifetime = useMemo(() => {
    const type = userData?.memberType || '';
    return type.includes('Lifetime') || type.includes('Seumur Hidup') || type.includes('Corporate') || type.includes('Korporat');
  }, [userData]);

  const yearlyRate = useMemo(() => {
    return fees.yearly[userData?.memberType] || 100;
  }, [fees, userData]);

  // Dynamic real-time calculated balance breakdown sheet outputs
  const breakdown = useMemo(() => {
    let regFee = 0;
    let mainFee = 0;
    
    if (paymentType === 'registration') {
      regFee = fees.registration;
      mainFee = isLifetime ? yearlyRate : yearlyRate * years;
    } else if (paymentType === 'renewal' || paymentType === 'tunggakan') {
      mainFee = isLifetime ? yearlyRate : yearlyRate * years;
    } else if (paymentType === 'other') {
      mainFee = parseFloat(customAmount) || 0;
    }

    return {
      regFee,
      mainFee,
      total: regFee + mainFee
    };
  }, [paymentType, fees, isLifetime, yearlyRate, years, customAmount]);

  // =========================================================================
  // ACTION OUTBOUND HANDLER
  // =========================================================================
  const handlePay = async () => {
    if (!userData || !auth.currentUser) return alert("Session expired. Please sign in again.");
    if (paymentType === 'other' && (!customPurpose.trim() || breakdown.total <= 0)) {
      return alert("Please explicitly state a valid payment purpose and amount.");
    }
    setIsProcessing(true);

    try {
      const secretRef = doc(db, "appSettings", "toyyibpay");
      const secretSnap = await getDoc(secretRef);

      if (!secretSnap.exists()) {
        throw new Error("Payment gateway parameters not found on server.");
      }

      // 🟢 Extract all 4 category configurations from database context
      const { 
        secretKey, 
        categoryCodeRegistration, 
        categoryCodeRenewal,
        categoryCodeTunggakan,
        categoryCodeOther
      } = secretSnap.data();

      // 🟢 Dynamic Category Selector Assignment
      let selectedCategoryCode = "";
      if (paymentType === 'registration') {
        selectedCategoryCode = categoryCodeRegistration;
      } else if (paymentType === 'renewal') {
        selectedCategoryCode = categoryCodeRenewal;
      } else if (paymentType === 'tunggakan') {
        selectedCategoryCode = categoryCodeTunggakan;
      } else if (paymentType === 'other') {
        selectedCategoryCode = categoryCodeOther;
      }

      // Fallback check to ensure the targeted code key isn't empty
      if (!selectedCategoryCode) {
        throw new Error(`ToyyibPay Category Code for "${paymentType.toUpperCase()}" is missing from settings.`);
      }

      const amountInCents = (breakdown.total * 100).toString();
      const yearsToPay = isLifetime ? 99 : years;

      // Construct descriptive narrative labels for ToyyibPay invoice records
      let billName = "PArM Payment Mode";
      let billDesc = `${userData?.parmNo || 'New Member'}`;
      
      if (paymentType === 'registration') {
        billName = "PArM Registration Fee";
        billDesc += ` ${userData?.memberType} (${yearsToPay} yr subscription)`;
      } else if (paymentType === 'renewal') {
        billName = "PArM Membership Renewal";
      } else if (paymentType === 'tunggakan') {
        billName = "PArM Arrears Settlement";
        billDesc += ` Settled Arrears: ${yearsToPay} Year(s)`;
      } else if (paymentType === 'other') {
        billName = "PArM Miscellaneous Payment";
        billDesc += ` ${customPurpose}`;
        // billDesc;
      }

      const formData = new FormData();
      formData.append("userSecretKey", secretKey);
      formData.append("categoryCode", selectedCategoryCode); // 🟢 Injects the correct distinct code mapped above
      formData.append("billName", billName);
      formData.append("billDescription", billDesc);
      formData.append("billPriceSetting", "1");
      formData.append("billPayorInfo", "1");
      formData.append("billAmount", amountInCents);
      formData.append("billChargeToCustomer", 0);
      
      // Pass contextual parameter tokens down through routing callback URLs
      formData.append("billReturnUrl", `https://www.parm.com.my/member-dashboard-parm?uid=${auth.currentUser.uid}&years=${yearsToPay}&action=${paymentType}`);
      formData.append("billCallbackUrl", `https://www.parm.com.my/member-dashboard-parm?uid=${auth.currentUser.uid}&years=${yearsToPay}&action=${paymentType}`);
      
      formData.append("billTo", userData?.name || "Member");
      formData.append("billEmail", userData?.email || "member@email.com");
      formData.append("billPhone", userData?.phone || "0123456789");

      const response = await axios.post(
        // "https://dev.toyyibpay.com/index.php/api/createBill", 
        "https://toyyibpay.com/index.php/api/createBill", 
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // const billCode = response.data[0]?.BillCode;
      // if (billCode) {
      //   const userRef = doc(db, "registrations", auth.currentUser.uid);
      //   await updateDoc(userRef, {
      //     currentBillCode: billCode,
      //     paymentStatus: "Pending" 
      //   });

      //   window.location.href = `https://dev.toyyibpay.com/${billCode}`;
      // } 

      const billCode = response.data[0]?.BillCode;
      if (billCode) {
        const userRef = doc(db, "registrations", auth.currentUser.uid);
        
        // 🟢 DYNAMIC STATUS MAPPING MATRIX
        let customPendingStatus = "Pending_Registration";
        if (paymentType === "renewal") customPendingStatus = "Pending_Renewal";
        if (paymentType === "tunggakan") customPendingStatus = "Pending_Tunggakan";
        if (paymentType === "other") customPendingStatus = "Pending_Other";

        await updateDoc(userRef, {
          currentBillCode: billCode,
          paymentStatus: customPendingStatus // Saved explicitly to their profile row
        });

        // window.location.href = `https://dev.toyyibpay.com/${billCode}`;
        window.location.href = `https://toyyibpay.com/${billCode}`;
      }
      else {
        console.error("toyyibPay gateway rejected layout payload:", response.data);
        setIsProcessing(false);
      }
    } catch (error) {
      console.error("Payment registration engine failed:", error);
      alert(`Initialization Failure: ${error.message}`);
      const userRef = doc(db, "registrations", auth.currentUser.uid);
        await updateDoc(userRef, {
          paymentStatus: "Failed" 
        });
      
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-wrapper">
        <div className="payment-card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#064e3b', fontWeight: 'bold' }}>Verifying Registration Assets...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="payment-wrapper">
        <div className="payment-card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#ff4a4a' }}>Profile access unauthenticated. Please re-login.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-wrapper">
      <div className="payment-card">
        <h2 className="payment-title">Membership Payment Portal</h2>
        <p className="payment-subtitle">Logged in as: <strong>{userData.name}</strong> {userData.parmNo && `(${userData.parmNo})`}</p>

        {/* 🟢 NEW SECTION: PAYMENT CATEGORY ROUTER SELECTOR */}
        <div className="payment-form-group-box">
        {/* PAYMENT CATEGORY ROUTER SELECTOR */}
          <div className="selection-group">
            <label>Payment Mode Option Purpose</label>
            <select 
              className="admin-table-inline-select" 
              value={paymentType} 
              onChange={(e) => {
                setPaymentType(e.target.value);
                setYears(1); 
              }}
              disabled={!isVerifiedMember} 
              style={{ width: '100%', padding: '10px', borderRadius: '20px', background: !isVerifiedMember ? '#f1f5f9' : '#fff' }}
            >
              {!isVerifiedMember && <option value="registration">New Membership Registration</option>}
              {isVerifiedMember && (
                <>
                  {/* 🟢 Hides the subscription renewals if user is Lifetime or Corporate */}
                  {!isLifetime && <option value="renewal">Annual Subscription Renewal</option>}
                  {!isLifetime && <option value="tunggakan">Arrears Settlement (Bayar Tunggakan)</option>}
                  
                  <option value="other">Other / Miscellaneous Fees</option>
                </>
              )}
            </select>
            
            {/* Contextual Warning Notes */}
            {!isVerifiedMember && (
              <p className="helper-text" style={{ color: '#b45309' }}>🔒 Options are locked. New accounts must clear standard entry initialization parameters.</p>
            )}
            {isVerifiedMember && isLifetime && (
              <p className="helper-text" style={{ color: '#047857' }}>✨ <strong>Note:</strong> As a {userData?.memberType}, annual subscription extensions are not required for your account status.</p>
            )}
          </div>
        </div>

        {/* Dynamic Parameter input configuration adjustments inside chosen layout flows */}
        {(paymentType === 'registration' || paymentType === 'renewal' || paymentType === 'tunggakan') && !isLifetime && (
          <div className="selection-group" style={{ marginTop: '15px' }}>
            <label>
              {paymentType === 'tunggakan' ? 'Number of Arrear Years to Settle' : 'Subscription Period (Years)'}
            </label>
            <div className="number-picker-custom">
              <button 
                type="button" className="picker-btn"
                onClick={() => setYears(prev => Math.max(1, prev - 1))}
                disabled={years <= 1}
              >
                −
              </button>
              <span className="picker-value">{years} {years === 1 ? 'Year' : 'Years'}</span>
              <button 
                type="button" className="picker-btn"
                onClick={() => setYears(prev => prev + 1)}
              >
                +
              </button>
            </div>
          </div>
        )}

        {/* Contextual blocks if selecting the custom input layout path option */}
        {paymentType === 'other' && (
          <div className="custom-payment-block" style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="payment-form-group-box">
              <label>Payment Purpose / Statement Description</label>
              <input 
                type="text" 
                className="payment-table-inline-input" 
                placeholder="e.g., Book purchase, Donation, Late Penalties..."
                value={customPurpose} 
                onChange={(e) => setCustomPurpose(e.target.value)}
                maxLength={70}
              />
            </div>
            <div className="payment-form-group-box">
              <label>Amount to Charge (RM)</label>
              <input 
                type="number" className="payment-table-inline-input" placeholder="0.00" min="1"
                value={customAmount} onChange={(e) => setCustomAmount(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Real-time reactive price breakdown matrix */}
        <div className="price-summary" style={{ marginTop: '25px' }}>
          {breakdown.regFee > 0 && (
            <div className="summary-row">
              <span>Registration Fee</span>
              <span>RM {breakdown.regFee}</span>
            </div>
          )}
          
          <div className="summary-row">
            <span>
              {paymentType === 'other' ? 'Custom Specified Charge' : ''}
              {paymentType === 'tunggakan' ? `Arrears Cumulative Total (${userData?.memberType})` : ''}
              {paymentType === 'renewal' ? `Subscription Renewal (${userData?.memberType})` : ''}
              {paymentType === 'registration' ? `Initial Subscription Term (${userData?.memberType})` : ''}
              {!isLifetime && paymentType !== 'other' && <small> x {years} yr(s)</small>}
              {isLifetime && paymentType !== 'other' && <small> (Lifetime Rate)</small>}
            </span>
            <span>RM {breakdown.mainFee}</span>
          </div>
          
          <div className="summary-divider"></div>
          <div className="summary-row total">
            <span>Total to Pay</span>
            <span>RM {breakdown.total}</span>
          </div>
        </div>

        <button 
          className="pay-btn" onClick={handlePay}
          disabled={isProcessing || breakdown.total <= 0}
          style={{ width: '100%', marginTop: '20px' }}
        >
          <CreditCard size={18} />
          {isProcessing ? "Connecting Gateway..." : `Confirm & Pay RM ${breakdown.total}`}
        </button>

        <div className="secure-info">
          <Info size={14} />
          <span>Secure payment window tracking processed via toyyibPay API.</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;